import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Image Lightbox with zoom (wheel + double-click + +/- keys),
 * keyboard nav (arrows/Escape), touch swipe, and thumbnail strip.
 *
 * Extracted from AdDetailPage.jsx (Feb 2026 refactor).
 */
export default function ImageLightbox({ images, currentIndex, onClose, onNext, onPrev, setCurrentImageIndex }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const imgRef = useRef(null);

  const minSwipeDistance = 50;

  const resetZoom = () => { setScale(1); setPosition({ x: 0, y: 0 }); };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') { resetZoom(); onNext(); }
      if (e.key === 'ArrowLeft') { resetZoom(); onPrev(); }
      if (e.key === '+' || e.key === '=') setScale(s => Math.min(s + 0.5, 4));
      if (e.key === '-') setScale(s => Math.max(s - 0.5, 1));
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, onNext, onPrev]);

  // Reset zoom when image changes
  useEffect(() => { resetZoom(); }, [currentIndex]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (scale > 1) { resetZoom(); }
    else { setScale(2.5); }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setScale(s => Math.min(Math.max(s + (e.deltaY > 0 ? -0.3 : 0.3), 1), 4));
  };

  const onTouchStart = (e) => {
    if (scale > 1) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    if (scale > 1) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || scale > 1) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0) { onNext(); }
      else { onPrev(); }
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center select-none"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-50"
        onClick={onClose}
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="absolute top-4 left-4 flex gap-2 z-50">
        <button
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          onClick={(e) => { e.stopPropagation(); setScale(s => Math.min(s + 0.5, 4)); }}
          data-testid="lightbox-zoom-in"
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <button
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          onClick={(e) => { e.stopPropagation(); setScale(s => Math.max(s - 0.5, 1)); }}
          data-testid="lightbox-zoom-out"
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        {scale > 1 && (
          <button
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs transition-colors"
            onClick={(e) => { e.stopPropagation(); resetZoom(); }}
          >
            {Math.round(scale * 100)}%
          </button>
        )}
      </div>

      {images.length > 1 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-40"
          onClick={(e) => { e.stopPropagation(); resetZoom(); onPrev(); }}
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      <div
        className="max-w-[90vw] max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          ref={imgRef}
          src={images[currentIndex]}
          alt={`Imagine ${currentIndex + 1}`}
          className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-200"
          style={{
            transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
            cursor: scale > 1 ? 'grab' : 'default'
          }}
          draggable={false}
        />
      </div>

      {images.length > 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-40"
          onClick={(e) => { e.stopPropagation(); resetZoom(); onNext(); }}
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {images.length > 1 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto px-4 py-2 rounded-xl bg-black/50 backdrop-blur-sm z-40">
          {images.map((img, i) => (
            <button
              key={`lightbox-thumb-${i}`}
              onClick={(e) => { e.stopPropagation(); resetZoom(); if (setCurrentImageIndex) setCurrentImageIndex(i); }}
              className={`flex-shrink-0 w-14 h-10 rounded-md overflow-hidden border-2 transition-all ${
                i === currentIndex ? "border-blue-500 opacity-100" : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
}
